<template>
  <div role="dialog" aria-label="Design" @keydown.esc="$emit('close')"
    class="w-[320px] max-w-[calc(100vw-1.5rem)] max-h-[calc(100vh-9rem)] overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-xl">
    <div class="px-4 py-3 space-y-4">
      <section>
        <h3 :class="LABEL">Template</h3>
        <TemplatePicker :model-value="template" :accent="settings.accent" columns="grid-cols-3" @update:model-value="$emit('template', $event)" />
      </section>

      <section>
        <h3 :class="LABEL">Accent colour</h3>
        <div class="flex flex-wrap items-center gap-2">
          <button v-for="color in SWATCHES" :key="color" type="button" @click="settings.accent = color" :aria-label="`Use ${color}`" :aria-pressed="settings.accent === color"
            class="w-6 h-6 rounded-full ring-offset-2 ring-offset-white dark:ring-offset-[#1C2128] transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            :class="settings.accent === color ? 'ring-2 ring-gray-900 dark:ring-white' : 'hover:ring-2 hover:ring-gray-300'" :style="{ backgroundColor: color }"></button>
          <label class="relative w-6 h-6 rounded-full overflow-hidden border border-dashed border-gray-300 dark:border-gray-600 cursor-pointer" title="Pick any colour">
            <span class="sr-only">Custom colour</span>
            <input type="color" :value="settings.accent" @input="settings.accent = $event.target.value" class="absolute -inset-2 w-10 h-10 cursor-pointer opacity-0">
            <svg class="w-full h-full p-1 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
          </label>
        </div>
      </section>

      <section>
        <h3 :class="LABEL">Font</h3>
        <SegmentedControl v-model="settings.font" label="Font" :options="FONT_OPTIONS" />
      </section>

      <div class="grid grid-cols-2 gap-3">
        <section>
          <h3 :class="LABEL">Text size</h3>
          <SegmentedControl v-model="settings.font_size" label="Text size" :options="SIZE_OPTIONS" />
        </section>
        <section>
          <h3 :class="LABEL">Spacing</h3>
          <SegmentedControl v-model="settings.spacing" label="Spacing" :options="SPACING_OPTIONS" />
        </section>
      </div>

      <section>
        <h3 :class="LABEL">Sections</h3>
        <p class="text-[11px] text-gray-400 dark:text-gray-500 -mt-1 mb-2">Set the order, and hide what you do not want on this resume.</p>
        <ul class="space-y-1">
          <li v-for="(key, index) in settings.section_order" :key="key" class="flex items-center gap-1 h-8 pl-2.5 pr-1 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-[#161b22]">
            <span class="flex-1 min-w-0 truncate text-[13px]" :class="isHidden(key) ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-800 dark:text-gray-100'">{{ labelFor(key) }}</span>
            <button type="button" @click="toggleHidden(key)" :aria-label="`${isHidden(key) ? 'Show' : 'Hide'} ${labelFor(key)}`" :aria-pressed="isHidden(key)" :class="ICON_BUTTON">
              <svg v-if="isHidden(key)" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
            </button>
            <button type="button" :disabled="index === 0" @click="move(index, -1)" :aria-label="`Move ${labelFor(key)} up`" :class="ICON_BUTTON">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/></svg>
            </button>
            <button type="button" :disabled="index === settings.section_order.length - 1" @click="move(index, 1)" :aria-label="`Move ${labelFor(key)} down`" :class="ICON_BUTTON">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>
            </button>
          </li>
        </ul>
      </section>
    </div>

    <div class="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-700">
      <button type="button" @click="resetLook" class="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white">Reset look</button>
      <button type="button" @click="$emit('close')" class="h-7 px-3 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700">Done</button>
    </div>
  </div>
</template>

<script setup>
import TemplatePicker from '../TemplatePicker.vue'
import SegmentedControl from './SegmentedControl.vue'
import { SECTION_LABELS } from './sectionDefs.js'

const LABEL = 'text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5'
const ICON_BUTTON = 'w-6 h-6 flex-shrink-0 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50'
const SWATCHES = ['#2563eb', '#0f766e', '#7c3aed', '#dc2626', '#ea580c', '#111827']
const FONT_OPTIONS = [{ value: 'sans', label: 'Sans' }, { value: 'serif', label: 'Serif' }, { value: 'mono', label: 'Mono' }]
const SIZE_OPTIONS = [{ value: 'small', label: 'S' }, { value: 'medium', label: 'M' }, { value: 'large', label: 'L' }]
const SPACING_OPTIONS = [{ value: 'compact', label: 'Tight' }, { value: 'normal', label: 'Normal' }, { value: 'relaxed', label: 'Airy' }]
const DEFAULTS = { accent: '#2563eb', font: 'sans', font_size: 'medium', spacing: 'normal' } // ResumeContent defaults

// `settings` and `custom` are the resume's own, edited in place.
const props = defineProps({
  settings: { type: Object, required: true },
  custom: { type: Array, default: () => [] },
  template: { type: String, default: 'modern' },
})
defineEmits(['close', 'template'])

const labelFor = (key) => SECTION_LABELS[key] || props.custom.find((section) => section.id === key)?.title || 'Untitled section'
const isHidden = (key) => props.settings.hidden_sections.includes(key)

function toggleHidden(key) {
  const hidden = props.settings.hidden_sections
  if (hidden.includes(key)) hidden.splice(hidden.indexOf(key), 1)
  else hidden.push(key)
}

function move(index, step) {
  const order = props.settings.section_order
  const [key] = order.splice(index, 1)
  order.splice(index + step, 0, key)
}

const resetLook = () => Object.assign(props.settings, DEFAULTS)
</script>
